import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { ListingPriceFragment } from '@graphql/__generated__/graphql';
import {
  formatAppraisalDiff,
  getListingPrices,
} from '@modules/_shared/utils/propertyTemplateFunctions';
import { useLocale } from '@shared/hooks/locale';

interface ListingPriceProps {
  listing?: ListingPriceFragment | null;
  isLoading?: boolean;
}

export const LISTING_PRICE_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingPrice on lots {
    offer_type
    sale_price
    price_unit
    rent_net
    rent_extra
    currency
    hide_price_on_portals
    property {
      id
      country_code
      computed_surface
      latest_appraisal {
        id
        value
        price_per_living_surface_meter_squared
      }
    }
  }
`);

export const ListingPriceSkeleton = () => {
  return (
    <Box>
      <Typography variant="h5">
        <Skeleton width={100} />
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: '-4px',
        }}
      >
        <Skeleton width={150} />
      </Typography>
    </Box>
  );
};

export const ListingPrice: React.FC<ListingPriceProps> = ({
  listing,
  isLoading = false,
}) => {
  const { t, locale } = useLocale();

  if (isLoading || listing == null) {
    return <ListingPriceSkeleton />;
  }

  const prices = getListingPrices(listing, t, locale);
  const primaryPrice =
    prices.primary != null ? (
      listing.hide_price_on_portals === true ? (
        <s>{prices.primary}</s>
      ) : (
        prices.primary
      )
    ) : (
      t('On request')
    );

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {primaryPrice}
        </Typography>
        {prices.appraisalDiff != null && (
          <Chip
            size="small"
            variant="outlined"
            color={prices.appraisalDiff > 0 ? 'error' : 'success'}
            label={formatAppraisalDiff(prices.appraisalDiff, locale)}
          />
        )}
      </Stack>
      <Typography variant="body2">{prices.secondary ?? '-'}</Typography>
    </Box>
  );
};
